import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    // Only listen for Escape when the accordion is used as a menu
    if (this.dataset.closeWithEscape === 'true') {
      this.addEventListener('keydown', this.#handleKeyDown, { signal });
    }

    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#setDefaultOpenState, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Blocks the browser's default toggle when the accordion is disabled at the current breakpoint.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const { disableOnMobile, disableOnDesktop } = this.dataset;
    const disabled = isMobileBreakpoint() ? disableOnMobile : disableOnDesktop;

    if (disabled === 'true') event.preventDefault();
  };

  /**
   * Closes the accordion when a click lands inside an element marked with `[data-accordion-close]`.
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    const { details } = this;

    if (!details.open || !(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    details.open = false;
  };

  /**
   * Sets the default open state from `open-by-default-on-mobile` / `open-by-default-on-desktop`.
   */
  #setDefaultOpenState = () => {
    const attribute = isMobileBreakpoint() ? 'open-by-default-on-mobile' : 'open-by-default-on-desktop';

    this.details.open = this.hasAttribute(attribute);
  };

  /**
   * Closes the accordion on Escape.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.summary.focus();
    this.details.open = false;
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}